/**
 * Beam — path-drawn structure (STRUCTURE round, 2026-08 — docs/bim-plan/08 Part 2).
 * DESIGNER-PLACED only: no auto-derived framing (rafters-at-spacing etc. stay a
 * recorded future direction), no footings (Addendum 09 §5).
 *
 * Z SEMANTICS — the levels-as-datums pattern this round sets: the beam's TOP rides the
 * level datum. topOfBeam(start) = level.elevationFt + startOffsetFt, topOfBeam(end) =
 * level.elevationFt + endOffsetFt, and the member's depth stacks DOWN from that — the
 * exact convention floors already follow ("top of assembly at level + offset,
 * thickness DOWN") and the mirror of the ROOF round's bearing-plane rule (anchor the
 * structurally meaningful face to the datum; the build-up grows away from it).
 * Unequal offsets = a sloped beam (roof framing); equal offsets = a level beam.
 *
 * `pathParamKey: 'path'` makes the beam a first-class path category: snap candidates,
 * host re-anchoring, whole-path translate, pathHandles node/edge grips — all derived
 * from the registry, no shared-file edits.
 */
import type { CategorySchema } from '../../registry'
import { TAIL_PROFILES } from '../../roofFraming'

export const beamSchema: CategorySchema = {
  id: 'beam',
  pickRank: 1,
  pathParamKey: 'path',
  label: 'Beam',
  typeParams: {
    groups: [
      {
        label: 'Section',
        params: [
          { key: 'widthFt', label: 'Width', kind: 'length', default: 3.5 / 12, min: 0.05, max: 4 },
          { key: 'depthFt', label: 'Depth', kind: 'length', default: 11.25 / 12, min: 0.05, max: 6 },
        ],
      },
      {
        label: 'Appearance',
        params: [
          // one material for the whole member — 3D color + cut poché (no assembly)
          { key: 'material', label: 'Material', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // COLORS (2026-10-05): the color brushed on the whole member with the Paint Brush (a
        // colors.ts id) — 3D only; the TYPE material above is what the member IS (poché, caps)
        label: 'Finish',
        params: [
          { key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // BEAM ENDS (2026-10-05, Matt: "make the ends of beams in AlderCAD have custom
        // shapes, like our rafter tails"): each end takes a style from the firm's end-style
        // chart — the SAME family rafter tails, outlooker brackets and barge ends use
        // (roofFraming.ts TAIL_PROFILES) — cut along the bottom over `…CutFt` of the
        // member (0 = twice the depth). Straight = the plain end, as before.
        label: 'Ends',
        params: [
          {
            key: 'startEndStyle', label: 'Start End', kind: 'choice', default: 'straight',
            choices: TAIL_PROFILES.map((p) => ({ value: p.key, label: p.label })),
          },
          { key: 'startCutFt', label: 'Start Cut Length', kind: 'length', default: 0, min: 0, max: 20 },
          {
            key: 'endEndStyle', label: 'End End', kind: 'choice', default: 'straight',
            choices: TAIL_PROFILES.map((p) => ({ value: p.key, label: p.label })),
          },
          { key: 'endCutFt', label: 'End Cut Length', kind: 'length', default: 0, min: 0, max: 20 },
        ],
      },
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Path', kind: 'path2d', default: null },
          {
            // the beam TOP at the path's first node, measured from the level datum
            key: 'startOffsetFt', label: 'Top @ Start', kind: 'length', default: 0, min: -40, max: 60,
          },
          {
            // …and at the last node; unequal offsets slope the member linearly by
            // walked arc length (the ramp model)
            key: 'endOffsetFt', label: 'Top @ End', kind: 'length', default: 0, min: -40, max: 60,
          },
        ],
      },
    ],
  },
}
